// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md
// Sopralluoghi list — wired to /appointments con quick filter tabs, rich
// table, paginazione e nuovo modal.

import { useState } from 'react'
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { useAppointments } from '../hooks/useAppointments'
import { useMe } from '../hooks/useAuth'
import { calendarDaysUntil } from '../lib/dateRelative'
import { canWriteParticipation } from '../lib/permissions'
import { AppointmentFormModal } from '../components/AppointmentFormModal'
import { CardSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import type { AppointmentRow } from '../lib/api'
import {
  STATUS_LABEL,
  STATUS_VARIANT,
  STATUS_DOT,
} from '../lib/sopralluogoStatus'
import { outcomeReasonLabel } from '../lib/outcomeReason'

type SopralluoghiSearch = {
  page: number
  page_size: number
  q: string
  status?: string
  scheduled_from?: string
  scheduled_to?: string
  data_asta_from?: string
  data_asta_to?: string
  created_from?: string
  created_to?: string
  include_ex_owners?: boolean
  da_gestire?: boolean
  client_id?: string
  sort: string
}

export const Route = createFileRoute('/sopralluoghi')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): SopralluoghiSearch => {
    const toNum = (v: unknown, dflt: number): number => {
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) && n > 0 ? n : dflt
    }
    const toStr = (v: unknown): string => (typeof v === 'string' ? v : '')
    const toOptStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.length > 0 ? v : undefined
    const toOptBool = (v: unknown): boolean | undefined => {
      if (v === true || v === 'true') return true
      if (v === false || v === 'false') return false
      return undefined
    }
    return {
      page: toNum(search.page, 1),
      page_size: toNum(search.page_size, 50),
      q: toStr(search.q),
      status: toOptStr(search.status),
      scheduled_from: toOptStr(search.scheduled_from),
      scheduled_to: toOptStr(search.scheduled_to),
      data_asta_from: toOptStr(search.data_asta_from),
      data_asta_to: toOptStr(search.data_asta_to),
      created_from: toOptStr(search.created_from),
      created_to: toOptStr(search.created_to),
      include_ex_owners: toOptBool(search.include_ex_owners),
      da_gestire: toOptBool(search.da_gestire),
      client_id: toOptStr(search.client_id),
      sort: toStr(search.sort) || '-scheduled_at',
    }
  },
  component: SopralluoghiPage,
})

function SopralluoghiPage() {
  // sopralluoghi.tsx acts as layout for /sopralluoghi/$id. Without questo
  // wrapper il parent renderizzava sempre la lista sovrascrivendo la detail
  // page child -> click freccia sembrava non aprire nulla (riportava a
  // "Tutti"). Stesso pattern di lotti.tsx + clienti.tsx.
  const matchRoute = useMatchRoute()
  const onChild = matchRoute({ to: '/sopralluoghi/$id' })
  if (onChild) return <Outlet />
  return <SopralluoghiList />
}

// STATUS_LABEL / STATUS_VARIANT / STATUS_DOT: single source of truth in
// ../lib/sopralluogoStatus (condiviso col detail).

const QUICK_TABS: Array<{
  key: string
  label: string
  // dateMode='inserimento' -> la colonna data mostra created_at ("Data
  // inserimento"); 'sopralluogo' -> scheduled_at ("Data sopr."). La data del
  // sopralluogo si conosce solo da 'attesa_riscontro_cliente' in poi.
  dateMode: 'inserimento' | 'sopralluogo'
  // sort opzionale (Fase 2e): 'asta_priority' = Data Asta asc + inserimento desc.
  sort?: string
  filter: {
    status?: string
    scheduled_from?: string
    scheduled_to?: string
    include_ex_owners?: boolean
    da_gestire?: boolean
  }
}> = [
  { key: 'tutti', label: 'Tutti', dateMode: 'inserimento', filter: {} },
  // Da gestire = task-based (Fase 2e): Richiesta fatta senza data con asta
  // ≤20gg, OPPURE Attesa conferma con scadenza superata. Ordine per Data Asta.
  {
    key: 'da-gestire',
    label: '🚨 Da gestire',
    dateMode: 'inserimento',
    sort: 'asta_priority',
    filter: { da_gestire: true },
  },
  // Stati del workflow (allineati al detail), in ordine.
  {
    key: 'richiesta-da-fare',
    label: 'Richiesta da fare',
    dateMode: 'inserimento',
    sort: 'asta_priority',
    filter: { status: 'richiesta_da_fare' },
  },
  {
    key: 'richiesta-fatta',
    label: 'Richiesta fatta',
    dateMode: 'inserimento',
    filter: { status: 'richiesta_fatta' },
  },
  {
    key: 'attesa',
    label: 'Attesa conferma Cliente',
    dateMode: 'sopralluogo',
    filter: { status: 'attesa_riscontro_cliente' },
  },
  {
    key: 'confermati',
    label: 'Sopralluogo Confermato',
    dateMode: 'sopralluogo',
    filter: { status: 'fissato' },
  },
  {
    key: 'effettuati',
    label: 'Effettuati',
    dateMode: 'sopralluogo',
    filter: { status: 'effettuato' },
  },
  // PROVVISORIO: set esiti negativi da rifinire con la lista di Marika (Fase 2).
  {
    key: 'non-effettuati',
    label: 'Non effettuati',
    dateMode: 'sopralluogo',
    filter: { status: 'non_effettuato,cancelled,noshow,sospeso' },
  },
  // Archivio owner ex (Jacopo, Patrizia) — sola lettura.
  {
    key: 'gestiti-passato',
    label: 'Gestiti in passato',
    dateMode: 'sopralluogo',
    filter: { include_ex_owners: true },
  },
]

function SopralluoghiList() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: me } = useMe()
  const canCreate = canWriteParticipation(me)
  const [newOpen, setNewOpen] = useState(false)

  const params = {
    page: search.page,
    page_size: search.page_size,
    q: search.q || undefined,
    status: search.status,
    scheduled_from: search.scheduled_from,
    scheduled_to: search.scheduled_to,
    data_asta_from: search.data_asta_from,
    data_asta_to: search.data_asta_to,
    created_from: search.created_from,
    created_to: search.created_to,
    include_ex_owners: search.include_ex_owners,
    da_gestire: search.da_gestire,
    client_id: search.client_id,
    sort: search.sort,
  }

  const { data, isLoading, isError } = useAppointments(params)
  const items = data?.items ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / search.page_size))

  // KPI dal subset visibile (count rapidi).
  const fissati = items.filter((s) => s.status === 'fissato').length
  const effettuati = items.filter((s) => s.status === 'effettuato').length
  const daFissare = items.filter((s) => s.status === 'da_fissare').length

  // Determine active tab from current search. I filtri data (asta/inserimento/
  // sopralluogo) sono ORTOGONALI ai tab → non entrano nel match, così non
  // deselezionano il tab quando l'utente restringe per data.
  const activeTabObj = QUICK_TABS.find((t) => {
    const f = t.filter
    return (
      (f.status ?? undefined) === (search.status ?? undefined) &&
      (f.include_ex_owners ?? undefined) ===
        (search.include_ex_owners ?? undefined) &&
      (f.da_gestire ?? undefined) === (search.da_gestire ?? undefined)
    )
  })
  const activeTab = activeTabObj?.key ?? 'tutti'
  // Colonna data: 'inserimento' (created_at) per gli stati pre-data,
  // 'sopralluogo' (scheduled_at) da 'Attesa conferma Cliente' in poi.
  const dateMode = activeTabObj?.dateMode ?? 'inserimento'

  function setTab(tab: (typeof QUICK_TABS)[number]) {
    // I filtri data (asta/inserimento/sopralluogo) restano: il tab cambia solo
    // status/da_gestire/include_ex_owners/sort.
    void navigate({
      search: (old: SopralluoghiSearch) => ({
        ...old,
        page: 1,
        status: tab.filter.status,
        include_ex_owners: tab.filter.include_ex_owners,
        da_gestire: tab.filter.da_gestire,
        sort: tab.sort ?? old.sort,
      }),
    })
  }

  function setDate(key: keyof SopralluoghiSearch, v: string) {
    void navigate({
      search: (old: SopralluoghiSearch) => ({
        ...old,
        page: 1,
        [key]: v || undefined,
      }),
    })
  }

  function resetDates() {
    void navigate({
      search: (old: SopralluoghiSearch) => ({
        ...old,
        page: 1,
        data_asta_from: undefined,
        data_asta_to: undefined,
        created_from: undefined,
        created_to: undefined,
        scheduled_from: undefined,
        scheduled_to: undefined,
      }),
    })
  }

  const hasDateFilters = !!(
    search.data_asta_from ||
    search.data_asta_to ||
    search.created_from ||
    search.created_to ||
    search.scheduled_from ||
    search.scheduled_to
  )

  function setSearchQ(q: string) {
    void navigate({
      search: (old: SopralluoghiSearch) => ({ ...old, page: 1, q }),
    })
  }

  function setPage(p: number) {
    void navigate({
      search: (old: SopralluoghiSearch) => ({ ...old, page: p }),
    })
  }

  // Ordinamento lista (#5): 1° click su header → crescente; 2° → decrescente.
  function setSort(field: string) {
    void navigate({
      search: (old: SopralluoghiSearch) => ({
        ...old,
        page: 1,
        sort: old.sort === field ? `-${field}` : field,
      }),
    })
  }
  const sortArrow = (field: string) =>
    search.sort === field ? ' ▲' : search.sort === `-${field}` ? ' ▼' : ''

  return (
    <>
      {/* Page header */}
      <div
        className="page-title-row"
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--fs-20)', fontWeight: 'var(--fw-semibold)' }}>
          Sopralluoghi
        </h1>
        <span className="muted total" style={{ fontSize: 'var(--fs-13)' }}>
          <b>{total.toLocaleString('it-IT')}</b> totali
          {fissati > 0 && (
            <>
              {' · '}
              <b>{fissati}</b> fissati
            </>
          )}
          {effettuati > 0 && (
            <>
              {' · '}
              <b>{effettuati}</b> effettuati
            </>
          )}
          {daFissare > 0 && (
            <>
              {' · '}
              <b>{daFissare}</b> da fissare
            </>
          )}
        </span>
        <span style={{ flex: 1 }} />
        {canCreate && (
          <button
            type="button"
            className="btn primary"
            onClick={() => setNewOpen(true)}
          >
            + Nuovo sopralluogo
          </button>
        )}
      </div>

      {/* Quick tabs */}
      <div
        className="savedviews"
        style={{
          display: 'flex',
          gap: 'var(--sp-2)',
          borderBottom: '1px solid var(--line)',
          marginBottom: 'var(--sp-3)',
          fontSize: 'var(--fs-12)',
        }}
      >
        {QUICK_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t)}
            className={`sv-tab ${activeTab === t.key ? 'active' : ''}`}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 6px',
              cursor: 'pointer',
              color:
                activeTab === t.key ? 'var(--ink-1)' : 'var(--ink-3)',
              fontWeight:
                activeTab === t.key
                  ? 'var(--fw-semibold)'
                  : 'var(--fw-regular)',
              borderBottom:
                activeTab === t.key
                  ? '2px solid var(--accent)'
                  : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search bar */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-2)',
          alignItems: 'center',
          marginBottom: 'var(--sp-2)',
        }}
      >
        <input
          type="search"
          placeholder="Cerca per cliente, lotto, custode…"
          value={search.q}
          onChange={(e) => setSearchQ(e.target.value)}
          style={{
            flex: '0 1 320px',
            padding: '6px 12px',
            fontSize: 'var(--fs-12)',
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--line)',
            background: 'var(--surface)',
          }}
        />
        <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
          {total.toLocaleString('it-IT')} {search.q ? 'risultati' : 'totali'}
        </span>
      </div>

      {/* Filtri data: 3 range indipendenti (asta / inserimento / sopralluogo),
          ortogonali ai tab. Param backend già pronti. */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-4)',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <DateRange
          label="Data Asta"
          from={search.data_asta_from}
          to={search.data_asta_to}
          onFrom={(v) => setDate('data_asta_from', v)}
          onTo={(v) => setDate('data_asta_to', v)}
        />
        <DateRange
          label="Data inserimento"
          from={search.created_from}
          to={search.created_to}
          onFrom={(v) => setDate('created_from', v)}
          onTo={(v) => setDate('created_to', v)}
        />
        <DateRange
          label="Data sopralluogo"
          from={search.scheduled_from}
          to={search.scheduled_to}
          onFrom={(v) => setDate('scheduled_from', v)}
          onTo={(v) => setDate('scheduled_to', v)}
        />
        {hasDateFilters && (
          <button
            type="button"
            onClick={resetDates}
            className="btn-ghost"
            style={{
              fontSize: 'var(--fs-11)',
              padding: '6px 10px',
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
          >
            ✕ Reset date
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading && <CardSkeleton />}
      {isError && (
        <div style={{ padding: 'var(--sp-4)' }} className="error">
          Errore caricamento sopralluoghi.
        </div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <EmptyState
          title="Nessun sopralluogo"
          body={
            search.q || search.status
              ? 'Nessun match per filtri attivi. Reset filtri o cambia tab.'
              : 'Non ci sono sopralluoghi ancora. Creane uno con + Nuovo sopralluogo.'
          }
        />
      )}
      {!isLoading && items.length > 0 && (
        <div
          className="table-wrap"
          style={{
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            background: 'var(--surface)',
            overflow: 'hidden',
          }}
        >
          <table
            className="data-table"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--fs-12)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-2)' }}>
                <Th>Codice</Th>
                <Th>
                  {(() => {
                    const f =
                      dateMode === 'inserimento'
                        ? 'created_at'
                        : 'scheduled_at'
                    const lbl =
                      dateMode === 'inserimento'
                        ? 'Data inserimento'
                        : 'Data sopr.'
                    return (
                      <button
                        type="button"
                        onClick={() => setSort(f)}
                        style={SORT_BTN_STYLE}
                        title="Ordina"
                      >
                        {lbl}
                        {sortArrow(f)}
                      </button>
                    )
                  })()}
                </Th>
                <Th>
                  <button
                    type="button"
                    onClick={() => setSort('data_asta')}
                    style={SORT_BTN_STYLE}
                    title="Ordina per data asta"
                  >
                    Data Asta{sortArrow('data_asta')}
                  </button>
                </Th>
                <Th>Cliente</Th>
                <Th>Lotto</Th>
                <Th>Custode</Th>
                <Th>Owner</Th>
                <Th>Stato</Th>
                <Th>Esito</Th>
                <Th>Note</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <SopralluogoRow key={a.id} a={a} dateMode={dateMode} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && pages > 1 && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: 'var(--sp-3)',
            fontSize: 'var(--fs-12)',
          }}
        >
          <span className="muted">
            Pagina <b>{search.page}</b> di <b>{pages}</b>
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={search.page <= 1}
            onClick={() => setPage(search.page - 1)}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="btn sm"
            disabled={search.page >= pages}
            onClick={() => setPage(search.page + 1)}
          >
            Next ›
          </button>
        </div>
      )}

      {newOpen && (
        <AppointmentFormModal
          open={newOpen}
          onClose={() => setNewOpen(false)}
        />
      )}
    </>
  )
}

// Header cliccabile per ordinamento (#5): eredita lo stile uppercase del Th.
const SORT_BTN_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  letterSpacing: 'inherit',
  textTransform: 'inherit',
  cursor: 'pointer',
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 10px',
        fontSize: 'var(--fs-10)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-caps)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--ink-3)',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {children}
    </th>
  )
}

// Filtro range data (from→to) per la barra filtri. Input date nativi.
function DateRange({
  label,
  from,
  to,
  onFrom,
  onTo,
}: {
  label: string
  from?: string
  to?: string
  onFrom: (v: string) => void
  onTo: (v: string) => void
}) {
  const inputStyle: React.CSSProperties = {
    padding: '5px 8px',
    fontSize: 'var(--fs-11)',
    borderRadius: 'var(--r-md)',
    border: '1px solid var(--line)',
    background: 'var(--surface)',
    color: 'var(--ink-1)',
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <span
        style={{
          fontSize: 'var(--fs-10)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: 'var(--ink-3)',
          fontWeight: 'var(--fw-semibold)',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <input
          type="date"
          value={from ?? ''}
          max={to || undefined}
          onChange={(e) => onFrom(e.target.value)}
          aria-label={`${label} da`}
          style={inputStyle}
        />
        <span style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-11)' }}>→</span>
        <input
          type="date"
          value={to ?? ''}
          min={from || undefined}
          onChange={(e) => onTo(e.target.value)}
          aria-label={`${label} a`}
          style={inputStyle}
        />
      </div>
    </div>
  )
}

function SopralluogoRow({
  a,
  dateMode,
}: {
  a: AppointmentRow
  dateMode: 'inserimento' | 'sopralluogo'
}) {
  const sCode = `S_${String(a.short_code).padStart(5, '0')}`
  // 'inserimento' -> created_at (giorno di creazione, senza ora);
  // 'sopralluogo' -> scheduled_at (data+ora del sopralluogo).
  const dateIso = dateMode === 'inserimento' ? a.created_at : a.scheduled_at
  // Urgenza Data Asta: badge se l'asta è entro <10gg (e non passata).
  const astaDays = a.property_data_asta
    ? calendarDaysUntil(new Date(a.property_data_asta))
    : null
  const astaUrgent = astaDays !== null && astaDays >= 0 && astaDays < 10
  const dt = dateIso ? new Date(dateIso) : null
  const dtStr = dt
    ? dateMode === 'inserimento'
      ? dt.toLocaleDateString('it-IT', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        })
      : `${dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' })} · ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`
    : '—'
  const relTime = dt ? relativeFromNow(dt) : null
  const status = a.status || ''
  const variant = STATUS_VARIANT[status] ?? 'outline'
  const statusLabel = STATUS_LABEL[status] ?? status.replace(/_/g, ' ')
  const cIni = a.client_name
    ? a.client_name
        .split(/\s+/)
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?'
  const cCode = a.client_short_code
    ? `C_${String(a.client_short_code).padStart(5, '0')}`
    : null
  const lCode = a.property_short_code
    ? `L_${String(a.property_short_code).padStart(5, '0')}`
    : null

  return (
    <tr
      style={{
        borderBottom: '1px solid var(--line)',
      }}
    >
      <td style={cellStyle}>
        <Link
          to="/sopralluoghi/$id"
          params={{ id: a.id }}
          search={{} as never}
          className="tag code"
          style={{
            fontSize: 'var(--fs-11)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--accent-ink)',
          }}
        >
          {sCode}
        </Link>
      </td>
      <td style={cellStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{dtStr}</span>
          {relTime && (
            <span
              className="muted"
              style={{
                fontSize: 'var(--fs-10)',
                color:
                  relTime.kind === 'today'
                    ? 'var(--accent-ink)'
                    : relTime.kind === 'past'
                      ? 'var(--ink-3)'
                      : 'var(--ink-2)',
              }}
            >
              {relTime.label}
            </span>
          )}
        </div>
      </td>
      <td style={cellStyle}>
        {a.property_data_asta ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--fs-12)',
              }}
            >
              {new Date(a.property_data_asta).toLocaleDateString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
              })}
            </span>
            {astaUrgent && (
              <span
                title="Asta imminente (<10 giorni)"
                style={{
                  alignSelf: 'flex-start',
                  fontSize: 'var(--fs-10)',
                  fontWeight: 'var(--fw-semibold)',
                  padding: '1px 6px',
                  borderRadius: '999px',
                  background: 'var(--risk-soft)',
                  border: '1px solid var(--risk-line)',
                  color: 'var(--risk-ink)',
                  whiteSpace: 'nowrap',
                }}
              >
                ⏰{' '}
                {astaDays === 0
                  ? 'oggi'
                  : astaDays === 1
                    ? 'domani'
                    : `tra ${astaDays}gg`}
              </span>
            )}
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        {a.client_name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
            <div
              className="avatar sm"
              style={{ background: avatarColor(a.client_id ?? a.id) }}
            >
              {cIni}
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 140,
                }}
              >
                {a.client_name}
              </div>
              {cCode && (
                <div
                  className="muted"
                  style={{ fontSize: 'var(--fs-10)', fontFamily: 'var(--font-mono)' }}
                >
                  {cCode}
                </div>
              )}
            </div>
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        {a.property_id ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Link
              to="/lotti/$id"
              params={{ id: a.property_id }}
              search={{} as never}
              style={{
                color: 'var(--ink-1)',
                fontWeight: 'var(--fw-medium)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
              }}
            >
              {a.property_address ?? '—'}
            </Link>
            {lCode && (
              <span
                className="muted"
                style={{ fontSize: 'var(--fs-10)', fontFamily: 'var(--font-mono)' }}
              >
                {lCode}
                {a.property_comune && ` · ${a.property_comune}`}
              </span>
            )}
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        {a.custode ? (
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 140,
              }}
            >
              {a.custode}
            </div>
            {a.custode_telefono && (
              <a
                href={`tel:${a.custode_telefono.replace(/\s/g, '')}`}
                className="muted"
                style={{
                  fontSize: 'var(--fs-10)',
                  fontFamily: 'var(--font-mono)',
                  textDecoration: 'none',
                }}
              >
                {a.custode_telefono}
              </a>
            )}
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        {a.owner_name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              className="avatar xs"
              style={{
                background: avatarColor(a.owner_id),
                width: 18,
                height: 18,
                fontSize: 9,
              }}
            >
              {a.owner_name
                .split(/\s+/)
                .map((p) => p[0])
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </div>
            <span style={{ fontSize: 'var(--fs-11)' }}>
              {a.owner_name.split(' ')[0]}
            </span>
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className={`chip ${variant}`} style={{ fontSize: 'var(--fs-10)' }}>
            <span
              className="dot"
              style={{ background: STATUS_DOT[variant] }}
            />
            {statusLabel}
          </span>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {a.modalita === 'remoto' && (
              <span
                className="chip info"
                style={{ fontSize: 'var(--fs-10)' }}
                title="Sopralluogo da remoto"
              >
                💻 Remoto
              </span>
            )}
            {a.esito_non_registrato && (
              <span
                className="chip warn"
                style={{ fontSize: 'var(--fs-10)' }}
                title="Data passata ma esito non ancora registrato"
              >
                ⚠️ Esito non registrato
              </span>
            )}
            {a.secondo_sollecito_dovuto && (
              <span
                className="chip warn"
                style={{ fontSize: 'var(--fs-10)' }}
                title="Custode già sollecitato, asta ≤10gg, non ancora in attesa → 2° sollecito"
              >
                📞 2° sollecito
              </span>
            )}
          </div>
          <SlaAndReminder a={a} />
        </div>
      </td>
      <td style={cellStyle}>
        {outcomeReasonLabel(a.outcome_reason) ? (
          <span style={{ fontSize: 'var(--fs-11)' }}>
            {outcomeReasonLabel(a.outcome_reason)}
          </span>
        ) : a.outcome ? (
          <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
            {a.outcome.replace(/_/g, ' ')}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        {a.notes ? (
          <span
            className="muted"
            title={a.notes}
            style={{
              fontSize: 'var(--fs-11)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              maxWidth: 240,
            }}
          >
            {a.notes}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={{ ...cellStyle, textAlign: 'right' }}>
        <Link
          to="/sopralluoghi/$id"
          params={{ id: a.id }}
          search={{} as never}
          className="btn sm ghost"
          style={{ padding: '2px 8px' }}
        >
          →
        </Link>
      </td>
    </tr>
  )
}

const cellStyle: React.CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'middle',
}

// SLA + reminder button.
// SLA logic: status richiesta_da_fare > 2gg = overdue.
// status richiesta_fatta > 5gg = overdue (custode non ha risposto).
// status attesa_riscontro_cliente > 3gg = overdue.
// Reminder click: apre wa.me al custode (priority) o cliente con template
// pre-asta default.
function SlaAndReminder({ a }: { a: AppointmentRow }) {
  const status = a.status
  const ageDays = a.data_richiesta
    ? Math.floor(
        (Date.now() - new Date(a.data_richiesta).getTime()) / 86_400_000,
      )
    : null
  const thresholdMap: Record<string, number> = {
    richiesta_da_fare: 2,
    richiesta_fatta: 5,
    attesa_riscontro_cliente: 3,
  }
  const threshold = thresholdMap[status]
  const needsAttention = threshold !== undefined
  if (!needsAttention) return null

  const overdue =
    ageDays !== null && threshold !== undefined && ageDays > threshold
  const slaLabel =
    ageDays === null
      ? '—'
      : ageDays === 0
        ? 'oggi'
        : ageDays === 1
          ? '1 giorno'
          : `${ageDays} giorni`

  function buildReminderBody(): string {
    const addr =
      a.property_address ?? a.property_comune ?? 'lotto'
    const auctionDate = a.property_data_asta
      ? new Date(a.property_data_asta).toLocaleDateString('it-IT')
      : null
    const lines: string[] = []
    if (status === 'richiesta_da_fare') {
      lines.push(`Salve, scrivo da AGL Roma EUR.`)
      lines.push(
        `Sto contattando per organizzare un sopralluogo al lotto ${addr}.`,
      )
      if (auctionDate) lines.push(`Asta prevista il ${auctionDate}.`)
      lines.push(`Mi farebbe sapere quando possiamo fissare visita?`)
    } else if (status === 'richiesta_fatta') {
      lines.push(`Buongiorno, sollecito gentile per il sopralluogo del lotto ${addr}.`)
      lines.push(`Resto in attesa di vostra disponibilità.`)
    } else {
      lines.push(`Buongiorno, sollecito conferma del sopralluogo ${addr}.`)
      lines.push(`Quando le sarebbe più comodo passare?`)
    }
    return lines.join('\n\n')
  }

  function onReminder(e: React.MouseEvent) {
    e.stopPropagation()
    e.preventDefault()
    const phone =
      (a.custode_telefono ?? a.client_phone ?? '').replace(/[^0-9]/g, '') || ''
    if (!phone) {
      window.alert('Nessun telefono custode o cliente disponibile.')
      return
    }
    const text = encodeURIComponent(buildReminderBody())
    const url = `https://wa.me/${phone}?text=${text}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--fs-10)',
          padding: '1px 6px',
          borderRadius: 'var(--r-sm)',
          fontWeight: 'var(--fw-medium)',
          background: overdue ? 'var(--risk-soft)' : 'var(--bg-3)',
          color: overdue ? 'var(--risk-ink)' : 'var(--ink-3)',
          border: overdue ? '1px solid var(--risk)' : '1px solid var(--line)',
          whiteSpace: 'nowrap',
        }}
        title={`In stato ${status} da ${slaLabel}`}
      >
        {overdue ? '⚠ ' : ''}
        {slaLabel}
      </span>
      <button
        type="button"
        onClick={onReminder}
        title="Invia reminder WhatsApp (custode o cliente)"
        style={{
          fontSize: 'var(--fs-10)',
          padding: '1px 6px',
          borderRadius: 'var(--r-sm)',
          border: '1px solid var(--accent)',
          background: 'var(--accent-soft)',
          color: 'var(--accent-ink)',
          fontWeight: 'var(--fw-semibold)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        📲 Reminder
      </button>
    </div>
  )
}

function relativeFromNow(d: Date): { kind: 'today' | 'soon' | 'past' | 'future'; label: string } {
  const calDays = calendarDaysUntil(d)
  if (calDays < -1) return { kind: 'past', label: `${-calDays}g fa` }
  if (calDays === -1) return { kind: 'past', label: 'ieri' }
  if (calDays === 0) return { kind: 'today', label: 'oggi' }
  if (calDays === 1) return { kind: 'today', label: 'domani' }
  if (calDays <= 7) return { kind: 'soon', label: `${calDays} gg` }
  return { kind: 'future', label: d.toLocaleDateString('it-IT') }
}

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  const hue = Math.abs(h) % 360
  return `oklch(58% 0.14 ${hue})`
}
